import { ref, onMounted, shallowRef } from 'vue'

// Shared singleton state for sound enabled - must be outside the composable
// so all components share the same mute state
const soundEnabled = ref(true)

export function useAudio() {
  const sounds = ref<Record<string, HTMLAudioElement>>({})
  const audioContext = shallowRef<AudioContext | null>(null)
  
  let lastScrollSoundTime = 0
  const SCROLL_SOUND_THROTTLE = 120
  let audioUnlocked = false

  onMounted(() => {
    const savedPref = localStorage.getItem('stack25-sound-enabled')
    if (savedPref !== null) {
      soundEnabled.value = savedPref === 'true'
    }

    // Preload custom sound files
    sounds.value = {
      cardPlacedYou: new Audio('/sounds/card-placed-you.mp3'),
      cardPlacedOpponent: new Audio('/sounds/card-placed-opponent.mp3'),
      cardScroll: new Audio('/sounds/card-scroll.mp3'),
      shuffle: new Audio('/sounds/shuffle.mp3'),
      victory: new Audio('/sounds/victory.mp3'),
      defeat: new Audio('/sounds/defeat.mp3')
    }
    
    // Set volumes
    Object.values(sounds.value).forEach(sound => {
      sound.volume = 0.5
      sound.preload = 'auto'
    })
    
    if (sounds.value.cardScroll) {
      sounds.value.cardScroll.volume = 0.3 // Quieter for frequent use
    }

    // Initialize audio on user interaction
    document.addEventListener('touchstart', unlockAudio, { once: true })
    document.addEventListener('click', unlockAudio, { once: true })
    document.addEventListener('touchstart', initAudioContext, { once: true })
    document.addEventListener('click', initAudioContext, { once: true })
  })

  const unlockAudio = () => {
    if (audioUnlocked) return
    
    const silent = sounds.value.cardScroll
    if (silent) {
      const originalVolume = silent.volume
      silent.volume = 0
      silent.play().then(() => {
        silent.pause()
        silent.currentTime = 0
        silent.volume = originalVolume
        audioUnlocked = true
      }).catch(() => {})
    }
  }
  
  const initAudioContext = () => {
    if (audioContext.value) return
    
    try {
      audioContext.value = new (window.AudioContext || (window as any).webkitAudioContext)()
      console.log('Audio context initialized')
    } catch (error) {
      console.error('Failed to create audio context:', error)
    }
  }

  // Play custom sound files
  const playAudioFile = (soundName: string) => {
    if (!soundEnabled.value) return
    
    const sound = sounds.value[soundName]
    if (sound) {
      // Create a new Audio instance each time to avoid conflicts
      // when sounds are played in rapid succession
      const newSound = new Audio(sound.src)
      newSound.volume = sound.volume
      newSound.play().catch(() => {})
    }
  }
  
  // Generate countdown sounds programmatically
  const generateCountdown = (ctx: AudioContext, number: number) => {
    const oscillator = ctx.createOscillator()
    const gainNode = ctx.createGain()
    
    // Different pitch for each number
    const frequency = 440 + (number * 50) // A4 + offset
    
    oscillator.type = 'sine'
    oscillator.frequency.value = frequency
    
    gainNode.gain.value = 0.4
    
    oscillator.connect(gainNode)
    gainNode.connect(ctx.destination)
    
    oscillator.start(ctx.currentTime)
    oscillator.stop(ctx.currentTime + 0.2) // 200ms duration
  }
  
  const generateCountdownGo = (ctx: AudioContext) => {
    const oscillator = ctx.createOscillator()
    const gainNode = ctx.createGain()
    
    oscillator.type = 'sine'
    oscillator.frequency.value = 880 // A5
    
    gainNode.gain.value = 0.4
    
    oscillator.connect(gainNode)
    gainNode.connect(ctx.destination)
    
    oscillator.start(ctx.currentTime)
    oscillator.stop(ctx.currentTime + 0.3) // 300ms duration
  }
  
  const playSynthSound = (generator: (ctx: AudioContext) => void) => {
    if (!soundEnabled.value) return
    if (!audioContext.value) {
      initAudioContext()
      if (!audioContext.value) return
    }
    
    try {
      generator(audioContext.value)
    } catch (error) {
      console.error('Error playing sound:', error)
    }
  }

  const playCardPlaced = (isYou: boolean) => {
    console.log(`Card placed sound triggered - isYou: ${isYou}`)
    // Use synthesized sound for card placement as well
    // This ensures it plays reliably even during rapid interactions
    playSynthSound((ctx) => {
      console.log('Inside playSynthSound for card placement')
      const oscillator = ctx.createOscillator()
      const gainNode = ctx.createGain()
      
      oscillator.type = 'sine'
      oscillator.frequency.value = isYou ? 800 : 600 // Higher pitch for your cards
      
      gainNode.gain.value = 0.3
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1)
      
      oscillator.connect(gainNode)
      gainNode.connect(ctx.destination)
      
      oscillator.start(ctx.currentTime)
      oscillator.stop(ctx.currentTime + 0.1) // 100ms duration
      console.log('Card placed sound started')
    })
    vibrate(isYou ? 50 : 30)
  }

  const playCardScroll = () => {
    const now = Date.now()
    if (now - lastScrollSoundTime > SCROLL_SOUND_THROTTLE) {
      // Use synthesized sound for card scrolling instead of audio file
      // This is more responsive and won't overrun the buffer
      playSynthSound((ctx) => {
        const oscillator = ctx.createOscillator()
        const gainNode = ctx.createGain()
        
        oscillator.type = 'sine'
        oscillator.frequency.value = 1200
        
        gainNode.gain.value = 0.2
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.05)
        
        oscillator.connect(gainNode)
        gainNode.connect(ctx.destination)
        
        oscillator.start(ctx.currentTime)
        oscillator.stop(ctx.currentTime + 0.05) // Very short duration (50ms)
      })
      vibrate(10)
      lastScrollSoundTime = now
    }
  }

  const playShuffle = () => {
    playAudioFile('shuffle')
  }

  const playCountdown = () => {
    playSynthSound((ctx) => generateCountdown(ctx, 3))
  }

  const playCountdownNumber = (num: number) => {
    if (num === 3) playSynthSound((ctx) => generateCountdown(ctx, 3))
    else if (num === 2) playSynthSound((ctx) => generateCountdown(ctx, 2))
    else if (num === 1) playSynthSound((ctx) => generateCountdown(ctx, 1))
    else if (num === 0) playSynthSound(generateCountdownGo)
  }

  const playVictory = () => {
    playAudioFile('victory')
    vibrate([100, 50, 100, 50, 200])
  }

  const playDefeat = () => {
    playAudioFile('defeat')
    vibrate([50, 30, 50])
  }

  const vibrate = (pattern: number | number[]) => {
    if ('vibrate' in navigator) {
      navigator.vibrate(pattern)
    }
  }

  const toggleSound = () => {
    soundEnabled.value = !soundEnabled.value
    localStorage.setItem('stack25-sound-enabled', String(soundEnabled.value))
  }

  return {
    soundEnabled,
    playCardPlaced,
    playCardScroll,
    playShuffle,
    playCountdown,
    playCountdownNumber,
    playVictory,
    playDefeat,
    toggleSound
  }
}
